import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Badge,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Label,
  Button,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import AdminHeader from "components/Headers/AdminHeader";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { urlDecodedId } from "components/CommonComponents/encodeDecodeFunction";
import TraineeCourses from "./TraineeCourses";
import TraineeCertificates from "./TraineeCertificates";
import { getTraineeDetailByID } from "action/trainee";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import { toCamelCase } from "components/CommonComponents/CommonFunctions";
import { downloadTraineeQRCode } from "action/trainee";
import { getChannelPartnerList } from "action/channelPartner";
import TraineeLetterListing from "./TraineeLetterListing";
import { TRAINEE_CATEGORY_OPTIONS, TRAINEE_TYPE_OPTIONS } from "components/constant";

const initialFields = {
  jointerID: "",
  fullName: "",
  email: "",
  mobile: "",
  gender: "",
  status: "",
  dob: "",
  createdOn: "",
  organization: "",
  enrollmentType: "",
  skills: [],
  qrCodePath: "",
  qrFileName: "",
  governmentID: "",
  state: "",
  city: "",
  pincode: "",
  addressLine1: "",
  addressLine2: "",
  landmark: "",
  traineeCategory: "",
  traineeType: "",
  channelPartner: "",
  documents: [],
};

export const downloadImage = (imageUrl, fileName = "image.jpg") => {
  const link = document.createElement("a");
  link.href = imageUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

function TraineeDetails() {
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const { id } = useParams();
  const decodedId = id ? urlDecodedId(id) : null;
  const { notify } = useContext(NotificationContext);
  const dispatch = useDispatch();
  const [data, setData] = useState(initialFields);
  const navigate = useNavigate();
  const location = useLocation();
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [activeTab, setActiveTab] = useState("details");

  const channelPartners =
    useSelector(
      (state) => state.channelPartner.channelPartnerList?.data?.channelPartners,
    ) || [];

  useEffect(() => {
    if (location?.state?.channel) {
      setData((prev) => ({
        ...prev,
        enrollmentType: "Institute",
        organization: "ABC Institute",
      }));
    }
  }, [location?.state?.channel]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setActiveTab(params.get("tab") || "details");
  }, [location.search]);

  useEffect(() => {
    getChannelPartnerList(dispatch);
  }, [dispatch]);

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      const params = new URLSearchParams(location.search);
      params.set("tab", tab);
      navigate(`${location.pathname}?${params.toString()}`, {
        replace: true,
      });
    }
  };

  useEffect(() => {
    if (!decodedId) return;

    const fetchDetails = async () => {
      try {
        const res = await getTraineeDetailByID(decodedId);

        if (res?.code === 200 && res?.data) {
          const trainee = res.data;

          setData({
            ID: trainee?.ID,
            userID: trainee?.userID,
            jointerID: trainee?.uUID || "-",
            fullName: `${trainee?.firstName || ""} ${trainee?.lastName || ""}`,
            email: trainee?.email || "-",
            mobile: trainee?.mobile || "-",
            gender: trainee?.gender ? toCamelCase(trainee.gender) : "-",
            status: trainee?.status || "-",
            dob: trainee?.dob || "-",
            createdOn: trainee?.createdAt || "-",
            organization: trainee?.organizationName || "",
            enrollmentType: trainee?.enrollmentType || "-",
            skills: trainee?.skills || [],
            qrCodePath: trainee?.qrCodePath || "",
            qrFileName: trainee?.qrFileName || "qr-code.png",
            profileImage: trainee?.profileImage || "",
            governmentID: trainee?.governmentID || "",
            state: trainee?.city?.state?.name || "-",
            city: trainee?.city?.name || "-",
            pincode: trainee?.pincode || "-",
            addressLine1: trainee?.lineOne || "-",
            addressLine2: trainee?.lineTwo || "-",
            landmark: trainee?.landmark || "-",
            traineeCategory: trainee?.traineeCategory || "-",
            traineeType: trainee?.traineeType || "-",
            channelPartner:
              channelPartners.find(
                (item) => item.ID === trainee?.channelPartnerID,
              )?.organizationName || "-",
            documents: trainee?.documents || [],
          });
        } else {
          notify("danger", res?.message || "Failed to load trainee details");
        }
      } catch (error) {
        notify("danger", "Something went wrong");
      }
    };

    fetchDetails();
  }, [decodedId, channelPartners]);

  const renderData = (label, value) => {
    return (
      <div className="mb-3 d-flex flex-column align-items-start ">
        <Label className="small-label text-muted mb-1">{label}</Label>
        <div className="small-text text-wrapper">{value || "-"}</div>
      </div>
    );
  };

  const downloadQR = async (trainee) => {
    setIsDownloading(true);
    try {
      const res = await downloadTraineeQRCode(trainee);
      if (res.status === 200 && res.data) {
        const fileName =
          res.headers["content-disposition"]?.match(/filename="(.+?)"/)?.[1] ||
          "qr-code.png";

        const url = URL.createObjectURL(res.data);

        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
      } else if (res.code === 404) {
        notify(
          "danger",
          "The QR code for this trainee was not found. Please regenerate it.",
        );
      } else {
        notify("danger", "Failed to download QR code. Please try again.");
      }
    } catch (error) {
      notify("danger", "Failed to download QR code. Please try again.");
    }
    setIsDownloading(false);
  };

  return (
    <>
      <AdminHeader />
      {isDownloading && (
        <div className="logout-overlay">
          <div className="logout-card">
            <i className="fa fa-spinner fa-spin fa-2x text-deep-blue mb-3" />
            <div className="font-weight-bold text-dark">
              Downloading… Please wait.
            </div>
          </div>
        </div>
      )}
      <Container fluid className="mt--6 course-layout">
        <Row>
          <Col md="12">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div
                className="d-flex align-items-center cursor text-white"
                onClick={() =>
                  navigate(
                    location?.state?.channel
                      ? role === "SUPER_ADMIN"
                        ? `/super-admin/channel-partner/${location?.state?.channel}/details`
                        : role === "CHANNEL_PARTNER"
                          ? `/channel-partner/channel-partner/${location?.state?.channel}/details`
                          : `/admin/channel-partner/${location?.state?.channel}/details`
                      : role === "SUPER_ADMIN"
                        ? "/super-admin/trainee/"
                        : role === "CHANNEL_PARTNER"
                          ? "/channel-partner/trainee/"
                          : "/admin/trainee/",
                  )
                }
              >
                <i className="fa-solid fa-angle-left mr-2" />
                <span className="font-weight-bold">Back</span>
              </div>
              <Button
                onClick={() =>
                  navigate(
                    role === "SUPER_ADMIN"
                      ? `/super-admin/trainee/edit/${id}`
                      : role === "CHANNEL_PARTNER"
                        ? `/channel-partner/trainee/edit/${id}`
                        : `/admin/trainee/edit/${id}`,
                    {
                      state: { mode: "edit" },
                    },
                  )
                }
              >
                <i className="fa fa-pencil mr-1" />
                Edit
              </Button>
            </div>

            <Card>
              <CardHeader className="course-sticky-header">
                <Row className="align-items-center">
                  {data?.profileImage && (
                    <Col
                      md="1"
                      className="d-flex justify-content-center align-items-center"
                    >
                      <img
                        src={data.profileImage}
                        alt="profile"
                        className="profile-circle"
                        onClick={() => setShowImageModal(true)}
                      />
                    </Col>
                  )}

                  <Col md="3">
                    <h2>
                      <span className="jointer-font-weight">
                        <span className="text-muted">Jointer ID:</span>{" "}
                        {data.jointerID || "-"}
                      </span>
                    </h2>
                  </Col>

                  <Col md="3">
                    <h2>
                      <span className="jointer-font-weight">
                        <span className="text-muted">Name:</span>
                      </span>{" "}
                      {data.fullName || "-"}
                    </h2>
                  </Col>
                </Row>
              </CardHeader>
            </Card>

            <Card>
              <CardHeader className="pb-0 course-sticky-header">
                <Nav className="custom-tabs cursor">
                  <NavItem>
                    <NavLink
                      className={activeTab === "details" ? "active" : ""}
                      onClick={() => toggleTab("details")}
                    >
                      Overview
                    </NavLink>
                  </NavItem>

                  <NavItem>
                    <NavLink
                      className={activeTab === "courses" ? "active" : ""}
                      onClick={() => toggleTab("courses")}
                    >
                      Enrolled Courses
                    </NavLink>
                  </NavItem>

                  <NavItem>
                    <NavLink
                      className={activeTab === "certificates" ? "active" : ""}
                      onClick={() => toggleTab("certificates")}
                    >
                      Earned Certificates
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === "letters" ? "active" : ""}
                      onClick={() => toggleTab("letters")}
                    >
                      Letters
                    </NavLink>
                  </NavItem>
                </Nav>
              </CardHeader>
            </Card>
            <TabContent activeTab={activeTab} className="trainee-tab-scroll">
              <TabPane className="course-details" tabId="details">
                <Row>
                  <Col md="8">
                    <Card>
                      <CardHeader>
                        <h4 className="mb-0">Basic Information</h4>
                      </CardHeader>
                      <CardBody
                      // style={{
                      //   height:
                      //     data.enrollmentType === "Institute"
                      //       ? "20rem"
                      //       : "20rem", //16rem
                      // }}
                      >
                        <Row>
                          <Col md="6" lg="4">
                            {renderData("Jointer ID", data.jointerID)}
                          </Col>
                          <Col md="6" lg="4">{renderData("Name", data.fullName)}</Col>
                          <Col md="6" lg="4">{renderData("Email", data.email)}</Col>
                          <Col md="6" lg="4">
                            {renderData("Mobile Number", data.mobile)}
                          </Col>
                          <Col md="6" lg="4">
                            {renderData("Date of Birth", data.dob)}
                          </Col>
                          <Col md="6" lg="4">{renderData("Gender", data.gender)}</Col>
                          <Col md="6" lg="4">
                            {renderData("Enrollment Type", data.enrollmentType)}
                          </Col>
                          {/* {data.enrollmentType === "Institute" && ( */}
                          <Col md="6" lg="4">
                            {renderData("Organization Name", data.organization)}
                          </Col>
                          <Col md="6" lg="4">
                            {renderData(
                              "Government ID",
                              data.governmentID || "-",
                            )}
                          </Col>
                          {/* )} */}
                          <Col md="6" lg="4">
                            {renderData("Registered On", data.createdOn)}
                          </Col>
                          <Col md="6" lg="4">
                            <div className="mb-3 d-flex flex-column align-items-start ">
                              <Label className="small-label text-muted mb-1">
                                Status
                              </Label>
                              <Badge
                                pill
                                color={
                                  data.status === "ACTIVE"
                                    ? "success"
                                    : "warning"
                                }
                                className="badge-lg"
                              >
                                {data.status}
                              </Badge>
                            </div>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>

                  <Col md="4">
                    <Card>
                      <CardHeader>
                        <h4 className="mb-0">QR Code</h4>
                      </CardHeader>

                      <CardBody
                      // style={{
                      //   height:
                      //     data.enrollmentType === "Institute"
                      //       ? "20rem"
                      //       : "20rem", //16rem
                      // }}
                      >
                        <Row className="align-items-center">
                          <Col md="12">
                            <div
                              className="svp-profile-qr">
                              <img
                                src={data?.qrCodePath}
                                alt="No QR Code available"

                              />
                              <span className="mt-3">Scan to verify</span>
                              <span className="cursor" onClick={() => downloadQR(data?.ID)}>Click to download</span>
                            </div>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                <Row>
                  <Col md="5">
                    <Card>
                      <CardHeader>
                        <h4 className="mb-0">Trainee Details</h4>
                      </CardHeader>

                      <CardBody>
                        <Row>
                          <Col md="6">{renderData("State", data.state)}</Col>

                          <Col md="6">{renderData("City", data.city)}</Col>

                          <Col md="6">
                            {renderData("Pincode", data.pincode)}
                          </Col>

                          <Col md="6">
                            {renderData("Landmark", data.landmark)}
                          </Col>

                          <Col md="12">
                            {renderData("Address Line 1", data.addressLine1)}
                          </Col>

                          <Col md="12">
                            {renderData("Address Line 2", data.addressLine2)}
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>

                  <Col md="7">
                    <Card className="w-100">
                      <CardHeader>
                        <h4 className="mb-0">Trainee Classification</h4>
                      </CardHeader>

                      <CardBody>
                        <Row>
                          <Col md="6" lg="4">
                            {renderData(
                              "Trainee Category",
                              TRAINEE_CATEGORY_OPTIONS.find(
                                (opt) => opt.value === data?.traineeCategory
                              )?.label || "-"
                            )}
                          </Col>

                          <Col md="6" lg="4">
                            {renderData(
                              "Trainee Type",
                              TRAINEE_TYPE_OPTIONS.find(
                                (opt) => opt.value === data?.traineeType
                              )?.label || "-",
                            )}
                          </Col>

                          <Col md="6" lg="4">
                            {renderData(
                              "Channel Partner",
                              data.channelPartner || "-",
                            )}
                          </Col>

                          <Col md="12">
                            <div className="mb-3 d-flex flex-column align-items-start w-100">
                              <Label className="small-label text-muted mb-2">
                                Prerequisites Documents
                              </Label>

                              {data?.documents?.length > 0 ? (
                                <div className="d-flex flex-wrap">
                                  {data.documents.map((doc, index) => {
                                    const isPdf = doc?.documentPath
                                      ?.toLowerCase()
                                      ?.includes(".pdf");

                                    return (
                                      <div
                                        key={index}
                                        className="position-relative mr-3 mb-3"
                                      >
                                        <a
                                          href={doc.documentPath}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="text-decoration-none"
                                        >
                                          <div className="doc-preview-container">
                                            <div className="doc-preview">
                                              {isPdf ? (
                                                <div className="pdf-preview">
                                                  <i className="fa fa-file-pdf text-danger fa-4x"></i>
                                                </div>
                                              ) : (
                                                <img
                                                  src={doc.documentPath}
                                                  className="doc-image"
                                                  alt="Document"
                                                />
                                              )}
                                            </div>

                                            <div className="doc-actions">
                                              <Button
                                                color="link"
                                                size="sm"
                                                className="doc-action-btn"
                                                title="View"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  window.open(
                                                    doc.documentPath,
                                                    "_blank",
                                                  );
                                                }}
                                              >
                                                <i className="fa fa-eye mr-5" />
                                              </Button>
                                            </div>
                                          </div>
                                        </a>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className="small-text">-</div>
                              )}
                            </div>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                <Row>
                  <Col md="7">
                    <Card>
                      <CardHeader>
                        <h4 className="mb-0">Skills (Static Data)</h4>
                      </CardHeader>

                      <CardBody className="cardStyle">
                        <Row className="align-items-center mb-3">
                          <Col md="12">
                            {data?.skills && data?.skills.length > 0 ? (
                              <div className="skills-div">
                                {data.skills.map((skill, i) => (
                                  <span key={i} className="skill-pill">
                                    {skill}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <div className="d-flex justify-content-center align-items-center py-3">
                                <span className="text-muted mt-4">
                                  No skills found
                                </span>
                              </div>
                            )}
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md="5">
                    <Card>
                      <CardHeader>
                        <h4 className="mb-0">Learning Summary (Static Data)</h4>
                      </CardHeader>

                      <CardBody className="cardStyle">
                        <Row className="align-items-center mb-3">
                          <Col md="12">
                            <div className="svp-profile-stats">
                              <div
                                className="stat-item enrolled cursor"
                                onClick={() => toggleTab("courses")}
                              >
                                <h2>9</h2>
                                <span>
                                  <i className="fas fa-book-open mr-1" />
                                  Enrolled Courses
                                </span>
                              </div>

                              <div
                                className="stat-item pending cursor"
                                onClick={() => toggleTab("courses")}
                              >
                                <h2>3</h2>
                                <span>
                                  <i className="fas fa-hourglass-half mr-1" />
                                  In_Progress Courses
                                </span>
                              </div>

                              <div
                                className="stat-item completed cursor"
                                onClick={() => toggleTab("courses")}
                              >
                                <h2>6</h2>
                                <span>
                                  <i className="fas fa-check-circle mr-1" />
                                  Completed Courses
                                </span>
                              </div>

                              <div
                                className="stat-item certificate cursor"
                                onClick={() => toggleTab("certificates")}
                              >
                                <h2>6</h2>
                                <span>
                                  <i className="fas fa-certificate mr-1" />
                                  Earned Certificates
                                </span>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </TabPane>

              <TabPane tabId="courses">
                <TraineeCourses />
              </TabPane>

              <TabPane tabId="certificates">
                <TraineeCertificates />
              </TabPane>

              <TabPane tabId="letters">
                {activeTab === "letters" && (
                  <TraineeLetterListing userID={data.userID} />
                )}
              </TabPane>
            </TabContent>
          </Col>
        </Row>
      </Container>
      {showImageModal && (
        <div
          className="image-modal-overlay"
          onClick={() => setShowImageModal(false)}
        >
          <div className="image-modal-content">
            <img src={data.profileImage} alt="profile-large" />
          </div>
        </div>
      )}
    </>
  );
}

export default TraineeDetails;
